import React, {
  Fragment,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { connect } from '../../../redux/store';
import t from 'prop-types';
import { I18n, generateKey } from '@kineticdata/react';
import {
  InfoMessage,
  openConfirm,
  usePrevious,
} from '@kineticdata/bundle-common';
import { parse } from 'query-string';
import classNames from 'classnames';
import {
  Dropdown,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { Map, OrderedMap, fromJS, is } from 'immutable';
import {
  buildContentFromSubmission,
  buildSubmissionValueFromContent,
  getValidSectionId,
  getContentOrderString,
  getContentIndexMap,
  Content,
  CONTENT_MANAGER_MODE,
} from './helpers';
import { getContentRenderer } from './renderers';

const LinkGroup = ({ parentId, ...props }) => {
  const isPublicMode = props.mode === CONTENT_MANAGER_MODE.PUBLIC;
  // Find active content item
  const activeItem = props.content.find(item => item.id === props.activeId);
  // Filter to the correct subset of items based on parentId
  const contentList = props.content.filter(
    item => (parentId ? item.parentId === parentId : !item.parentId),
  );

  return contentList && contentList.size > 0 ? (
    <div className="side-nav__group">
      {contentList
        // Only show settings sections for non-PUBLIC modes
        .filter(item => !isPublicMode || !item.settings)
        // Map each item at the current level to a button
        .map(item => {
          const hasChildren = !!props.content.find(
            ci => (!isPublicMode || !ci.settings) && item.id === ci.parentId,
          );
          const renderChildren =
            !isPublicMode ||
            item.id === props.activeId ||
            (!!activeItem && activeItem.ancestorIds.includes(item.id));
          const sectionIndex =
            !!props.contentIndexMap && props.contentIndexMap.get(item.id);
          return (
            <Fragment key={item.id}>
              <button
                className={classNames({ active: item.id === props.activeId })}
                onClick={props.onClick(item.id)}
              >
                {item.settings && (
                  <span className="fa fa-sm fa-fw text-muted fa-cog" />
                )}
                <span className="label">
                  {sectionIndex && (
                    <small className="text-muted">{sectionIndex}</small>
                  )}
                  {item.title ? (
                    <I18n>{item.title}</I18n>
                  ) : (
                    <em>
                      <I18n>Missing Title</I18n>
                    </em>
                  )}
                </span>
                {isPublicMode &&
                  hasChildren && (
                    <span
                      className={classNames(
                        'fa fa-sm fa-fw fa-angle-right fa-animate text-muted ml-auto',
                        { 'fa-rotate-90': renderChildren },
                      )}
                    />
                  )}
                {!isPublicMode &&
                  item.isDirty && (
                    <span
                      className={classNames(
                        'ml-auto fa fa-fw fa-sm fa-pencil-alt text-info',
                      )}
                    />
                  )}
              </button>
              {/* Render any children that may exist if flag is true */}
              {renderChildren && (
                <LinkGroup
                  key={`children-${item.id}`}
                  {...props}
                  parentId={item.id}
                />
              )}
            </Fragment>
          );
        })
        .toList()}
    </div>
  ) : null;
};

const LinkGroupMobile = ({ parentId, ...props }) => {
  const [openMenu, setOpenMenu] = useState(false);
  const toggleMenu = useCallback(() => setOpenMenu(open => !open), []);

  const isPublicMode = props.mode === CONTENT_MANAGER_MODE.PUBLIC;
  // Find active content item
  const activeItem = props.content.find(item => item.id === props.activeId);
  // Find current item, which is the active item or it's ancestor in the current level
  const currentItem = !!activeItem
    ? (!activeItem.parentId && !parentId) || activeItem.parentId === parentId
      ? activeItem
      : props.content.find(
          item =>
            (!!parentId ? item.parentId === parentId : !item.parentId) &&
            activeItem.ancestorIds.includes(item.id),
        )
    : null;
  // Filter to the correct subset of items based on parentId
  const contentList = props.content.filter(
    item => (parentId ? item.parentId === parentId : !item.parentId),
  );
  const currentSectionIndex =
    !!props.contentIndexMap &&
    currentItem &&
    props.contentIndexMap.get(currentItem.id);

  return contentList && contentList.size > 0 ? (
    <div className="side-nav__group">
      <Dropdown
        className="side-nav__dropdown"
        isOpen={!!openMenu}
        toggle={toggleMenu}
      >
        <DropdownToggle
          color="dropdown"
          className={classNames('side-nav__toggle', {
            active: !!currentItem,
          })}
        >
          {currentItem &&
            currentItem.settings && (
              <span className="fa fa-sm fa-fw text-muted fa-cog" />
            )}
          <span className="label">
            {currentSectionIndex && (
              <small className="text-muted">{currentSectionIndex}</small>
            )}
            {currentItem ? (
              currentItem.title ? (
                <I18n>{currentItem.title}</I18n>
              ) : (
                <em>
                  <I18n>Missing Title</I18n>
                </em>
              )
            ) : (
              <em>
                <I18n>Select a Subsection</I18n>
              </em>
            )}
          </span>
          {!isPublicMode &&
            currentItem &&
            currentItem.isDirty && (
              <span className="fa fa-fw fa-sm fa-pencil-alt text-info ml-2" />
            )}
          <span className={`ml-auto fa fa-fw fa-angle-down text-muted`} />
        </DropdownToggle>
        <DropdownMenu>
          {currentItem &&
            currentItem.parentId && (
              // Only show clear option for subsections to allow returning to
              // viewing parent section content
              <>
                <button
                  onClick={e => {
                    props.onClick(currentItem.parentId)(e);
                    toggleMenu();
                  }}
                >
                  <span className="fa fa-fw fa-times text-muted" />
                  <em className="label text-muted">
                    <I18n>Clear Subsection</I18n>
                  </em>
                </button>
                <div className="side-nav__divider" />
              </>
            )}
          {contentList
            // Only show settings sections for non-PUBLIC modes
            .filter(item => !isPublicMode || !item.settings)
            // Map each item at the current level to a button
            .map(item => {
              const hasChildren = !!props.content.find(
                ci => item.id === ci.parentId,
              );
              const sectionIndex =
                !!props.contentIndexMap && props.contentIndexMap.get(item.id);
              return (
                <Fragment key={item.id}>
                  <button
                    className={classNames({
                      active: item.id === props.activeId,
                    })}
                    onClick={e => {
                      props.onClick(item.id)(e);
                      toggleMenu();
                    }}
                  >
                    {item.settings && (
                      <span className="fa fa-sm fa-fw text-muted fa-cog" />
                    )}
                    <span className="label">
                      {sectionIndex && (
                        <small className="text-muted">{sectionIndex}</small>
                      )}
                      {item.title ? (
                        <I18n>{item.title}</I18n>
                      ) : (
                        <em>
                          <I18n>Missing Title</I18n>
                        </em>
                      )}
                    </span>
                    {!isPublicMode &&
                      item.isDirty && (
                        <span
                          className={classNames(
                            'fa fa-fw fa-sm fa-pencil-alt text-info ml-2',
                          )}
                        />
                      )}
                    {hasChildren && (
                      <span
                        className={classNames(
                          ' ml-auto fa fa-sm fa-fw fa-angle-right text-muted',
                        )}
                      />
                    )}
                  </button>
                </Fragment>
              );
            })
            .toList()}
        </DropdownMenu>
      </Dropdown>
      {currentItem && <LinkGroupMobile {...props} parentId={currentItem.id} />}
    </div>
  ) : null;
};

// Creates a list of all sections in their correct order to display them all
// simultaneously. This can only be used for PUBLIC mode.
const createSinglePageViewList = (content, parentId, contentIndexMap) => {
  // Filter to the correct subset of items based on parentId
  const contentList = content
    .filter(
      item =>
        !item.settings &&
        (parentId ? item.parentId === parentId : !item.parentId),
    )
    .toList()
    .toJS();
  return contentList.reduce(
    (list, item) => [
      ...list,
      {
        ...item,
        sectionIndex: contentIndexMap && contentIndexMap.get(item.id),
        Renderer: getContentRenderer(item.rendererId || item.id),
      },
      ...createSinglePageViewList(content, item.id, contentIndexMap),
    ],
    [],
  );
};

const ContentManagerComponent = ({
  submission,
  imageDir,
  mode,
  save,
  username,
  actionsPending,
  onDirtyChange,
  isMobile,
  indexSections,
  enableSearch,
  singlePageView,
  initialSectionName,
}) => {
  // Store previous state of the submission so we can check for changes
  const prevSubmission = usePrevious(submission);
  // State for initial submission values and content to use for checking if
  // changes were made and for resetting changes
  const [valuesInit, setValuesInit] = useState(null);
  const [contentInit, setContentInit] = useState(null);
  // State for editable versions of values and content that will be modified
  // when in EDIT mode
  const [values, setValues] = useState(valuesInit);
  const [content, setContent] = useState(contentInit);
  // Ref for container to scroll to on section change
  const containerRef = useRef(null);

  // State for tracking the currently open section and a unique key for the
  // markdown editor so we can rerender it when needed
  const [[activeId, rendererKey], setActiveId] = useState([
    null,
    generateKey(),
  ]);
  // Updater and handler for updating the currently active content section
  const updateActiveId = useCallback(id => setActiveId([id, generateKey()]), [
    setActiveId,
  ]);
  const handleActiveIdUpdate = useCallback(id => () => updateActiveId(id), [
    updateActiveId,
  ]);
  // Scroll to container when activeId changes
  useEffect(
    () => {
      if (
        activeId &&
        containerRef.current &&
        containerRef.current.getBoundingClientRect().top < 0
      ) {
        containerRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [activeId],
  );

  // Create a string defining the order of the sections that can be used to
  // determine which reorder operations are valid for the current section.
  // Also used for determining the isDirty flag.
  const contentOrderString = getContentOrderString(content);

  // Track dirty state so we know when the user has made any changes.
  const isDirty =
    content && values
      ? !is(fromJS(content.toJS()), fromJS(contentInit.toJS())) ||
        contentOrderString !== getContentOrderString(contentInit) ||
        !is(fromJS(values.toJS()), fromJS(valuesInit.toJS()))
      : false;
  // Track out of sync state to know when the submission was updated by someone
  // else while the user has the content manager open.
  const [isOutOfSync, setIsOutOfSync] = useState(false);
  useEffect(
    () => {
      if (typeof onDirtyChange === 'function') {
        onDirtyChange(isDirty);
      }
      // If dirty flag is set to false, reset out of sync flag since everything
      // should be in sync now.
      if (!isDirty) {
        setIsOutOfSync(false);
      }
    },
    [isDirty, onDirtyChange],
  );

  // Get current active content item and its renderer
  const activeItem = content ? content.get(activeId) : null;
  const activeRendererId = activeItem ? activeItem.rendererId : null;
  const Renderer = useMemo(
    () => getContentRenderer(activeRendererId || activeId),
    [activeRendererId, activeId],
  );

  // When the submission changes, check for content changes and reset
  // appropriate state.
  useEffect(
    () => {
      if (
        !!submission !== !!prevSubmission ||
        (submission && !is(fromJS(submission), fromJS(prevSubmission)))
      ) {
        // Build maps of values and content from the submission, and reset the
        // init states of each with the latest data.
        const newValues = Map(submission.values)
          .delete('Content')
          .delete('Locked By');
        const newContent = fromJS(buildContentFromSubmission(submission));
        setValuesInit(newValues);
        setContentInit(newContent);
        // If the data was changed by another user, set isOutOfSync flag,
        // otherwise set values and content from the new submission.
        setValues(oldValues => {
          // If in EDIT mode, and the submission was updated and values were
          // changed, keep the old values so we don't clear the user's changes.
          // This could happen when the lock is renewed, or if another user
          // makes changes. Otherwise, set the new values into state.
          if (
            mode === CONTENT_MANAGER_MODE.EDIT &&
            !!oldValues &&
            !is(
              // Map any empty strings to null since that's what the api returns
              fromJS(oldValues.map(v => (v === '' ? null : v)).toJS()),
              fromJS(newValues.toJS()),
            )
          ) {
            // If updated by a different user, set isOutOfSync flag
            if (submission.updatedBy !== username) {
              setIsOutOfSync(true);
            }
            return oldValues;
          } else {
            return newValues;
          }
        });
        setContent(oldContent => {
          // If in EDIT mode, and the submision was updated and content is
          // different, keep the old content so we don't clear the user's
          // changes. This could happen when the lock is renewed, or if another
          // user makes changes. Otherwise, set the new content into state.
          // Also update the activeId to rerender the editor.
          if (
            mode === CONTENT_MANAGER_MODE.EDIT &&
            !!oldContent &&
            !is(
              // Reset isDirty flag since the saved version will have it false
              fromJS(oldContent.map(c => c.set('isDirty', false)).toJS()),
              fromJS(newContent.toJS()),
            )
          ) {
            // If updated by a different user, set isOutOfSync flag
            if (submission.updatedBy !== username) {
              setIsOutOfSync(true);
            }
            setActiveId(([aId]) => [
              getValidSectionId(oldContent, mode, aId, initialSectionName),
              generateKey(),
            ]);
            return oldContent;
          } else {
            setActiveId(([aId]) => [
              getValidSectionId(newContent, mode, aId, initialSectionName),
              generateKey(),
            ]);
            return newContent;
          }
        });
      }
    },
    [submission, prevSubmission, isDirty, mode, username, initialSectionName],
  );

  // State for stoirng if page is about to be printed so we can change the UI.
  const [printView, setPrintView] = useState(false);
  // Handlers for setting the printView state
  const handleBeforePrint = useCallback(() => setPrintView(true), []);
  const handleAfterPrint = useCallback(() => setPrintView(false), []);
  // Event listeners for setting the printView state
  useEffect(() => {
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  });

  // Handler for adding a new content section
  const handleContentAdd = useCallback(
    parentId => () => {
      const id = generateKey();
      setContent(c => {
        const parent = c.get(parentId);
        return c.set(
          id,
          Content({
            id,
            parentId: parent ? parent.id : undefined,
            ancestorIds: parent
              ? [parent.id, ...parent.ancestorIds]
              : undefined,
            isDirty: true,
            rendererId: 'NEW',
          }),
        );
      });
      updateActiveId(id);
    },
    [updateActiveId],
  );
  // Handler for deleting a section, and all its children
  const handleContentDelete = useCallback(
    id => () => {
      openConfirm({
        title: 'Delete Section',
        body:
          'Are you sure you want to delete this section? Any descendant sections will also be deleted.',
        actionName: 'Delete',
        ok: () =>
          setContent(c => {
            // Filter out the removed record and any of its descendants
            const newContent = c.filter(
              c => c.id !== id && !c.ancestorIds.includes(id),
            );
            // Set activeId to first section that still exists
            if (activeId === id) {
              updateActiveId(getValidSectionId(newContent, mode));
            }
            // Return new content map
            return newContent;
          }),
      });
    },
    [activeId, updateActiveId, mode],
  );

  // Handler for updating a part of a content item
  const handleContentUpdate = useCallback((id, key, value) => {
    setContent(c =>
      c.update(id, i =>
        i
          .set(key, value)
          .set('isDirty', true)
          .set('updatedAt', new Date().toISOString()),
      ),
    );
  }, []);
  // Event handler for updating a part of a content item, with the value taken
  // from an event object.
  const handleContentChange = useCallback(
    (id, key) => e => handleContentUpdate(id, key, e.target.value),
    [handleContentUpdate],
  );
  // Event handler for updating a part of a content item, with the value
  // provided directly.
  const handleContentChangeChecked = useCallback(
    (id, key, value, defaultValue = '') => e =>
      handleContentUpdate(id, key, e.target.checked ? value : defaultValue),
    [handleContentUpdate],
  );
  // Event handler for updating a part of a content item, with the value
  // provided directly.
  const handleContentChangeDirect = useCallback(
    (id, key) => value => handleContentUpdate(id, key, value),
    [handleContentUpdate],
  );

  // Determine which reorder operations are valid for the current section
  const contentOrderOptions = useMemo(
    () => {
      const contentOrder = contentOrderString
        .split(';')
        .map(el => el.split(','));
      const currentIdx = contentOrder.findIndex(el => el[0] === activeId);
      if (currentIdx < 0) {
        return {};
      }
      const parentId = contentOrder[currentIdx][1];
      const siblingIdxs = contentOrder
        .map(
          (el, idx) => (el[1] === parentId && el[0] !== activeId ? idx : null),
        )
        .filter(idx => idx !== null);

      const canMove = {
        canMoveUp: siblingIdxs.length > 0 && siblingIdxs[0] < currentIdx,
        canMoveDown:
          siblingIdxs.length > 0 &&
          siblingIdxs[siblingIdxs.length - 1] > currentIdx,
        canMoveOut: !!parentId,
        canMoveIn:
          contentOrder[currentIdx][2] !== 's' &&
          siblingIdxs.length > 0 &&
          siblingIdxs.find(idx => idx > currentIdx) >= 0 &&
          contentOrder[siblingIdxs.find(idx => idx > currentIdx)][2] !== 's',
      };

      return {
        ...canMove,
        currentIndex: currentIdx,
        moveUpIndex: canMove.canMoveUp
          ? siblingIdxs.filter(i => i < currentIdx).pop()
          : null,
        moveDownIndex: canMove.canMoveDown
          ? siblingIdxs.find(i => i > currentIdx)
          : null,
        moveOutIndex: canMove.canMoveOut
          ? contentOrder.findIndex(el => el[0] === parentId)
          : null,
        moveOutNewParent: canMove.canMoveOut
          ? contentOrder.find(el => el[0] === parentId)[1]
          : null,
        moveInIndex: canMove.canMoveIn
          ? siblingIdxs.find(i => i > currentIdx)
          : null,
        moveInNewParent: canMove.canMoveIn
          ? contentOrder[siblingIdxs.find(i => i > currentIdx)][0]
          : null,
      };
    },
    [contentOrderString, activeId],
  );
  // Event handler for updating the position of a section within the content.
  const handleContentReorder = useCallback(
    direction => () =>
      setContent(c => {
        const contentList = c.toList();
        const item = contentList.get(contentOrderOptions.currentIndex);
        if (item) {
          let updatedList = contentList;
          if (direction === 'up' && contentOrderOptions.moveUpIndex !== null) {
            updatedList = contentList
              .delete(contentOrderOptions.currentIndex)
              .insert(contentOrderOptions.moveUpIndex, item);
          } else if (
            direction === 'down' &&
            contentOrderOptions.moveDownIndex !== null
          ) {
            updatedList = contentList
              .delete(contentOrderOptions.currentIndex)
              .insert(contentOrderOptions.moveDownIndex, item);
          } else if (
            direction === 'out' &&
            contentOrderOptions.moveOutIndex !== null
          ) {
            updatedList = contentList
              .delete(contentOrderOptions.currentIndex)
              .insert(
                contentOrderOptions.moveOutIndex,
                item.set('parentId', contentOrderOptions.moveOutNewParent),
              );
          } else if (
            direction === 'in' &&
            contentOrderOptions.moveInIndex !== null
          ) {
            updatedList = contentList
              .delete(contentOrderOptions.currentIndex)
              .insert(
                contentOrderOptions.moveInIndex,
                item.set('parentId', contentOrderOptions.moveInNewParent),
              );
          }
          return updatedList.reduce(
            (map, section) => map.set(section.id, section),
            OrderedMap(),
          );
        } else {
          console.error(
            'handleContentReorder ERROR',
            contentOrderOptions.currentIndex,
            'No current item',
          );
          return c;
        }
      }),
    [contentOrderOptions],
  );
  // Map of section ids to their index values, used if indexSections is true
  const contentIndexMap = useMemo(
    () => {
      return !!indexSections ? getContentIndexMap(contentOrderString) : null;
    },
    [indexSections, contentOrderString],
  );

  // Handler for updating a field value
  const handleValuesUpdate = useCallback((id, name, value) => {
    setValues(v => v.set(name, value));
    setContent(c =>
      c.update(id, i =>
        i.set('isDirty', true).set('updatedAt', new Date().toISOString()),
      ),
    );
  }, []);
  // Event handler for updating a field value, with the value taken from an
  // event object. Used for simple text fields.
  const handleValuesChange = useCallback(
    (id, name) => e => handleValuesUpdate(id, name, e.target.value),
    [handleValuesUpdate],
  );
  // Event handler for updating a field value, using the checked event property
  // to determine if the value should be used. Used for simple checkbox fields.
  const handleValuesChangeChecked = useCallback(
    (id, name, value, defaultValue) => e =>
      handleValuesUpdate(id, name, e.target.checked ? value : defaultValue),
    [handleValuesUpdate],
  );
  // Event handler for updating a field value, with the value provided directly.
  // Used for fields with more complex values.
  const handleValuesChangeDirect = useCallback(
    (id, name) => value => handleValuesUpdate(id, name, value),
    [handleValuesUpdate],
  );

  // Event handler for reseting all changes in the current section
  const handleResetSection = useCallback(
    () => {
      if (activeItem) {
        openConfirm({
          title: 'Reset Section Changes',
          body: 'Are you sure you want to reset your changes in this section?',
          actionName: 'Reset',
          ok: () => {
            // Get the saved version from contentInit, or if this is a newely added
            // section, overwrite it with empty values
            const previousVersion =
              (contentInit && contentInit.get(activeId)) ||
              Content({
                id: activeId,
                parentId: activeItem.parentId,
                ancestorIds: activeItem.ancestorIds,
                isDirty: true,
                rendererId: 'NEW',
              });
            setContent(c => c.set(activeId, previousVersion));
            // Update any fields that are used by this section's renderer
            if (Renderer && typeof Renderer.FIELDS === 'object') {
              setValues(v =>
                Map(Renderer.FIELDS).reduce((uv, fv, fn) => {
                  const origValue = valuesInit.get(fn);
                  return typeof origValue !== 'undefined'
                    ? uv.set(fn, origValue)
                    : uv.delete(fn);
                }, v),
              );
            }
            updateActiveId(activeId);
          },
        });
      }
    },
    [contentInit, valuesInit, activeId, activeItem, updateActiveId, Renderer],
  );
  // Event handler for reseting all changes in the current section
  const handleResetAll = useCallback(
    () => {
      openConfirm({
        title: 'Reset All Changes',
        body: 'Are you sure you want to reset all your changes?',
        actionName: 'Reset',
        ok: () => {
          setContent(contentInit);
          setValues(valuesInit);
          setIsOutOfSync(false);
          updateActiveId(getValidSectionId(contentInit, mode, activeId));
        },
      });
    },
    [contentInit, valuesInit, activeId, updateActiveId, mode],
  );

  // Event handler for saving all changes
  const handleSaveAll = useCallback(
    () => {
      if (typeof save === 'function') {
        save({
          submissionId: submission.id,
          updatedAt: submission.updatedAt,
          values: {
            ...values.toJS(),
            Content: buildSubmissionValueFromContent(content),
          },
        });
      } else {
        console.error(
          'ContentManager was not provided a `save` function as a prop.',
        );
      }
    },
    [submission, save, values, content],
  );

  // Object of common props that will be passed to all Renderers
  const commonRendererProps = {
    mode,
    content: mode === CONTENT_MANAGER_MODE.EDIT ? content : contentInit,
    values: mode === CONTENT_MANAGER_MODE.EDIT ? values : valuesInit,
    // (id, name) => e => updaterFn using e.target.value
    handleContentChange,
    // (id, name, value, defaultValue) => e => updaterFn using
    // e.target.checked to pick between value and defaultValue
    handleContentChangeChecked,
    // (id, name) => value => updaterFn using value
    handleContentChangeDirect,
    // (id, name) => e => updaterFn using e.target.value
    handleValuesChange,
    // (id, name, value, defaultValue) => e => updaterFn using
    // e.target.checked to pick between value and defaultValue
    handleValuesChangeChecked,
    // (id, name) => value => updaterFn using value
    handleValuesChangeDirect,
    handleResetSection,
    handleContentAdd,
    handleContentDelete,
    handleActiveIdUpdate,
    actionsPending,
    // The directory path to where images for this entity are stored
    // within the S3 bucket used by the markdown component
    imageDir,
    // Map of section ids to their index, only available if the
    // indexSections prop is true
    contentIndexMap,
    // Type, defined as the name of the submission's form
    type: submission.form.name,
  };

  // Build JSX content
  const jsx =
    content &&
    ((mode === CONTENT_MANAGER_MODE.PUBLIC ||
      mode === CONTENT_MANAGER_MODE.MANAGE) &&
    (singlePageView || printView) ? (
      // If in PUBLIC or MANAGE mode, and singlePageView or printView, render
      // all sections in one view without any navigation
      <div className="column-container content-manager" ref={containerRef}>
        <div className="column-panel column-panel--large">
          {createSinglePageViewList(content, null, contentIndexMap).map(
            item =>
              item.Renderer && (
                <div className="mb-5" key={item.id}>
                  <item.Renderer
                    key={`singlepageview-${item.id}-${mode}`}
                    {...commonRendererProps}
                    activeItem={item}
                    // Current section's index value, only available if the
                    // indexSections prop is true
                    sectionIndex={item.sectionIndex}
                  />
                </div>
              ),
          )}
        </div>
      </div>
    ) : printView ? (
      // If not in PUBLIC or MANAGE mode, but in printView, show message saying
      // this mode cannot be printed.
      <div className="column-container content-manager">
        <div className="column-panel column-panel--large">
          <InfoMessage
            title="This content cannot be printed."
            message="You cannot print this content while in edit or review mode."
          />
        </div>
      </div>
    ) : (
      // If not in PUBLIC or MANAGE mode, or not singlePageView or printView,
      // render navigation and the currently active section
      <>
        {isOutOfSync &&
          mode === CONTENT_MANAGER_MODE.EDIT && (
            <div className="alert alert-warning alert-bar">
              <I18n>
                This revision has been updated by another user since you've
                opened it. Saving your changes may overwrite changes made by
                others. Click Reset All Changes to clear your updates and reset
                the revision to the latest version.
              </I18n>
            </div>
          )}

        <div className="column-container content-manager" ref={containerRef}>
          <div
            className={classNames('column-panel column-panel--small', {
              'mb-4': isMobile,
            })}
          >
            <div className="side-nav">
              {isMobile ? (
                <LinkGroupMobile
                  mode={mode}
                  content={content}
                  activeId={activeId}
                  onClick={handleActiveIdUpdate}
                  contentIndexMap={contentIndexMap}
                />
              ) : (
                <LinkGroup
                  mode={mode}
                  content={content}
                  activeId={activeId}
                  onClick={handleActiveIdUpdate}
                  contentIndexMap={contentIndexMap}
                />
              )}
              {enableSearch && (
                <>
                  <div className="side-nav__divider" />
                  <div
                    className={classNames({
                      'side-nav__group': !isMobile,
                      'side-nav__dropdown': isMobile,
                    })}
                  >
                    <button
                      className={classNames({
                        active: activeId === 'SEARCH',
                        'btn btn-dropdown': isMobile,
                      })}
                      onClick={handleActiveIdUpdate('SEARCH')}
                    >
                      <span className="fa fa-sm fa-fw text-muted fa-search" />
                      <span className="label">
                        <I18n>Search</I18n>
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
            {mode === CONTENT_MANAGER_MODE.EDIT && (
              <div className="d-flex mt-4">
                <button
                  className="btn btn-primary flex-grow-1 mr-2"
                  style={{ maxWidth: '12rem' }}
                  disabled={actionsPending || !isDirty}
                  onClick={handleSaveAll}
                >
                  <span
                    className={`fa ${
                      actionsPending ? 'fa-spinner fa-spin' : 'fa-save'
                    } fa-fw mr-1`}
                  />
                  Save All Changes
                </button>
                <UncontrolledDropdown className="d-inline-block ml-auto">
                  <DropdownToggle
                    title="Additional Actions"
                    color="outline-dark"
                  >
                    <span className="fa fa-angle-down" />
                  </DropdownToggle>
                  <DropdownMenu right>
                    <DropdownItem
                      tag="button"
                      onClick={handleContentAdd()}
                      disabled={actionsPending}
                    >
                      <span className="fa fa-plus fa-fw fa-sm" />
                      Add Root Section
                    </DropdownItem>
                    {activeItem &&
                      activeItem.parentId && (
                        <DropdownItem
                          tag="button"
                          onClick={handleContentAdd(activeItem.parentId)}
                          disabled={actionsPending || activeItem.settings}
                        >
                          <span className="fa fa-plus fa-fw fa-sm" />
                          Add Sibling Section
                        </DropdownItem>
                      )}
                    {activeItem && (
                      <DropdownItem
                        tag="button"
                        onClick={handleContentAdd(activeId)}
                        disabled={actionsPending || activeItem.settings}
                      >
                        <span className="fa fa-plus fa-fw fa-sm" />
                        Add Child Section
                      </DropdownItem>
                    )}
                    <DropdownItem divider />
                    {!isMobile && (
                      <>
                        <button
                          className="dropdown-item"
                          onClick={handleContentReorder('up')}
                          disabled={!contentOrderOptions.canMoveUp}
                        >
                          <span className="fa fa-arrow-up fa-fw fa-sm" />
                          Move Section Up
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={handleContentReorder('down')}
                          disabled={!contentOrderOptions.canMoveDown}
                        >
                          <span className="fa fa-arrow-down fa-fw fa-sm" />
                          Move Section Down
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={handleContentReorder('out')}
                          disabled={!contentOrderOptions.canMoveOut}
                        >
                          <span className="fa fa-level-up-alt fa-flip-horizontal fa-fw fa-sm" />
                          Move Section Out
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={handleContentReorder('in')}
                          disabled={!contentOrderOptions.canMoveIn}
                        >
                          <span className="fa fa-level-up-alt fa-flip-vertical fa-fw fa-sm" />
                          Move Section In
                        </button>
                        <DropdownItem divider />
                      </>
                    )}
                    <DropdownItem
                      tag="button"
                      onClick={handleResetAll}
                      disabled={actionsPending || !isDirty}
                    >
                      <span className="fa fa-trash fa-fw fa-sm" />
                      Reset All Changes
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            )}
          </div>
          <div className="column-panel column-panel--large">
            {Renderer && (
              <Renderer
                key={`${rendererKey}-${activeId}-${mode}`}
                {...commonRendererProps}
                activeItem={activeItem}
                // Current section's index value, only available if the
                // indexSections prop is true
                sectionIndex={contentIndexMap && contentIndexMap.get(activeId)}
              />
            )}
          </div>
        </div>
      </>
    ));

  return jsx;
};

export const ContentManager = connect(state => ({
  isMobile: ['small', 'medium'].includes(state.app.layoutSize),
  initialSectionName: (parse(state.router.location.search) || {}).section,
}))(ContentManagerComponent);

ContentManager.propTypes = {
  // Submission record to show in the ContentManager
  submission: t.object.isRequired,
  // String used to prefix the images shown and uploaded in the markdown widget
  imageDir: t.string,
  // Mode of the ContentManager
  mode: t.number,
  // Save function that will update the submission with the provided values
  save: t.func,
  // The username of the current user, required for EDIT mode
  username: t.string,
  // Boolean defining whether any user actions are pending
  actionsPending: t.bool,
  // Function to call when the dirty flag changes
  onDirtyChange: t.func,
  // Flag that determines whether the sections will be numbered in the menu
  indexSections: t.bool,
  // Flag that determines whether searching through the content is available
  enableSearch: t.bool,
};
